import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import type {
  TenantSummary,
  TenantDetail,
  WorkspaceDetail,
  WorkspaceSummary,
  WorkspaceSnapshots,
  ActionResult,
  DropletSize,
  Tier,
  TenantStatus,
  Environment,
} from './types';

const keys = {
  list: ['tenants'] as const,
  detail: (id: string) => ['tenants', id] as const,
  workspace: (tenantId: string, wsId: string) =>
    ['tenants', tenantId, 'workspaces', wsId] as const,
};

/* ── Reads ─────────────────────────────────────────────────────────── */

export function useTenants() {
  return useQuery({
    queryKey: keys.list,
    queryFn: () => apiFetch<TenantSummary[]>('/tenants'),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useTenant(id: string | undefined) {
  return useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => apiFetch<TenantDetail>(`/tenants/${id}`),
    enabled: Boolean(id),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** Plan panel data (MC-609). `tier` is the saved registry value verbatim —
 *  a misconfigured non-Tier string stays visible rather than being masked. */
/** Per-tenant adjustments (MC-610). Key present = overridden;
 *  present-with-null = explicitly unlimited. */
export interface PlanOverrides {
  max_instances?: number | null;
  max_size?: string | null;
  entitlement?: {
    limits?: { max_users?: number | null; max_plugins?: number | null };
    features?: Record<string, boolean>;
  };
}

export interface TenantPlan {
  tier: string;
  plans: { slug: string; name: string }[];
  /** Effective values — plan defaults with overrides applied. */
  limits: { max_instances: number | null; max_size: string | null };
  usage: { instances: number };
  overrides: PlanOverrides | null;
  entitlement: {
    version: number;
    tier: string;
    limits: { max_users: number | null; max_plugins: number | null };
    plugins: string | string[];
    smtp: unknown;
    ssl: string;
    features: Record<string, boolean>;
  };
}

export function useTenantPlan(id: string | undefined) {
  return useQuery({
    // Keyed under the tenant detail so the patch hook's invalidation
    // refetches this too (prefix match).
    queryKey: [...keys.detail(id ?? ''), 'plan'],
    queryFn: () => apiFetch<TenantPlan>(`/tenants/${id}/plan`),
    enabled: Boolean(id),
  });
}

export function useWorkspace(tenantId: string | undefined, workspaceId: string | undefined) {
  return useQuery({
    queryKey: keys.workspace(tenantId ?? '', workspaceId ?? ''),
    queryFn: () => apiFetch<WorkspaceDetail>(`/tenants/${tenantId}/workspaces/${workspaceId}`),
    enabled: Boolean(tenantId && workspaceId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/* ── Mutations on a workspace ──────────────────────────────────────── */

export function useRestartWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, reason }: { tenantId: string; workspaceId: string; reason: string }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/restart`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
    },
  });
}

export function useResizeWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, droplet_size, reason }: {
      tenantId: string; workspaceId: string; droplet_size: DropletSize; reason: string;
    }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/resize`, {
        method: 'POST',
        body: JSON.stringify({ droplet_size, reason }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
    },
  });
}

export function useWorkspaceSnapshots(tenantId: string | undefined, workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['tenants', tenantId, 'workspaces', workspaceId, 'snapshots'],
    queryFn: () => apiFetch<WorkspaceSnapshots>(`/tenants/${tenantId}/workspaces/${workspaceId}/snapshots`),
    enabled: Boolean(tenantId && workspaceId),
    staleTime: 15_000,
    // Poll fast while a snapshot is in-flight; slower at rest.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data && data.in_progress.length > 0 ? 10_000 : 60_000;
    },
  });
}

export function useSnapshotWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, label }: { tenantId: string; workspaceId: string; label?: string }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/snapshot`, {
        method: 'POST',
        body: JSON.stringify({ label }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      // Refetch snapshots so the new in-progress entry appears immediately.
      qc.invalidateQueries({
        queryKey: ['tenants', tenantId, 'workspaces', workspaceId, 'snapshots'],
      });
    },
  });
}

export function useSuspendWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, reason }: { tenantId: string; workspaceId: string; reason: string }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export function useResumeWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId }: { tenantId: string; workspaceId: string }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/resume`, {
        method: 'POST',
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export interface ProvisionWorkspaceBody {
  subdomain: string;
  name: string;
  environment: 'production' | 'staging' | 'development';
  region: 'lon1' | 'fra1' | 'nyc3';
  droplet_size: DropletSize;
}

export function useProvisionWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, ...body }: { tenantId: string } & ProvisionWorkspaceBody) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

/* ── Custom domains ────────────────────────────────────────────────── */

export function useAddCustomDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, domain }: {
      tenantId: string; workspaceId: string; domain: string;
    }) =>
      apiFetch(`/tenants/${tenantId}/workspaces/${workspaceId}/domains`, {
        method: 'POST',
        body: JSON.stringify({ domain }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
    },
  });
}

/* ── Tenant CRUD ───────────────────────────────────────────────────── */

export interface CreateTenantBody {
  slug: string;
  name: string;
  owner_name: string;
  owner_email: string;
  operator_owner?: string;
  tier?: Tier;
  notes?: string;
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantBody) =>
      apiFetch<TenantDetail>(`/tenants`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export interface PatchTenantBody {
  name?: string;
  owner_name?: string;
  owner_email?: string;
  operator_owner?: string;
  tier?: Tier;
  status?: TenantStatus;
  notes?: string;
  /** Replaces the stored adjustments wholesale; null clears them all. */
  plan_overrides?: PlanOverrides | null;
}

/** MC-612 — refused by the backend while the customer has instances. */
export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, confirm, reason }: { tenantId: string; confirm: string; reason: string }) =>
      apiFetch<void>(`/tenants/${tenantId}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirm, reason }),
      }),
    onSuccess: (_, { tenantId }) => {
      // Drop the tenant from the cached list immediately so the redirect
      // never renders a deleted customer next to its deletion toast, and
      // stop every query under its key so nothing refetches the dead slug
      // into a 404 (FH-010).
      qc.setQueryData<TenantSummary[]>(keys.list, old =>
        old?.filter(t => t.slug !== tenantId && t.id !== tenantId),
      );
      qc.removeQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
      qc.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}

export function usePatchTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, ...body }: { tenantId: string } & PatchTenantBody) =>
      apiFetch<TenantDetail>(`/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

/* ── Workspace edit ────────────────────────────────────────────────── */

export interface PatchWorkspaceBody {
  name?: string;
  tags?: string[];
  environment?: Environment;
}

export function usePatchWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, ...body }: {
      tenantId: string; workspaceId: string;
    } & PatchWorkspaceBody) =>
      apiFetch<WorkspaceSummary>(`/tenants/${tenantId}/workspaces/${workspaceId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
    },
  });
}

/* ── Destroy + clone ───────────────────────────────────────────────── */

export function useDestroyWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, confirm, reason }: {
      tenantId: string; workspaceId: string; confirm: string; reason: string;
    }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/destroy`, {
        method: 'POST',
        body: JSON.stringify({ confirm, reason }),
      }),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export interface CloneWorkspaceBody {
  subdomain: string;
  name: string;
  environment: 'production' | 'staging' | 'development';
  droplet_size?: DropletSize;
}

export function useCloneWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, ...body }: {
      tenantId: string; workspaceId: string;
    } & CloneWorkspaceBody) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/clone`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_, { tenantId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

export function useSyncDns() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId }: { tenantId: string; workspaceId: string }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/dns/sync`, {
        method: 'POST',
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
    },
  });
}

/* ── Provisioning progress + rebuild ───────────────────────────────── */

export interface ProvisionPhase {
  status: string;
  message: string;
  at: string;
}

export interface WorkspaceProgress {
  workspace_id: string;
  provision_status: string;
  provision_progress: string;
  provision_started_at: string | null;
  provision_history: ProvisionPhase[];
  elapsed_sec: number | null;
  stalled: boolean;
  last_error: string | null;
  last_heartbeat_at: string | null;
  last_droplet_ip: string | null;
  hostname?: string | null;
}

/** Live phase-by-phase progress for the stepper. Polls every 5s while
 *  the workspace is provisioning; pass enabled=false once it's live. */
export function useWorkspaceProgress(
  tenantId: string | undefined,
  workspaceId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: [...keys.workspace(tenantId ?? '', workspaceId ?? ''), 'progress'],
    queryFn: () =>
      apiFetch<WorkspaceProgress>(`/tenants/${tenantId}/workspaces/${workspaceId}/progress`),
    enabled: Boolean(tenantId && workspaceId) && enabled,
    refetchInterval: 5_000,
  });
}

/** Destroy the droplet (best-effort) and provision a fresh one with the
 *  same hostname — the one-click recovery path for failed provisions. */
export function useRebuildWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, reason }: {
      tenantId: string; workspaceId: string; reason: string;
    }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/rebuild`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.list });
    },
  });
}

/* ── Upgrade (spooled ops-runner) ──────────────────────────────────── */

export interface UpgradeStatus {
  status: 'idle' | 'queued' | 'running' | 'success' | 'failed';
  job_id?: string;
  started_at?: string | null;
  finished_at?: string | null;
  log_tail?: string;
}

export function useUpgradeWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, reason }: {
      tenantId: string; workspaceId: string; reason: string;
    }) =>
      apiFetch<ActionResult>(`/tenants/${tenantId}/workspaces/${workspaceId}/upgrade`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({
        queryKey: [...keys.workspace(tenantId, workspaceId), 'upgrade'],
      });
    },
  });
}

/** Polls fast while an upgrade is queued/running, otherwise sits idle. */
export function useUpgradeStatus(
  tenantId: string | undefined,
  workspaceId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: [...keys.workspace(tenantId ?? '', workspaceId ?? ''), 'upgrade'],
    queryFn: () =>
      apiFetch<UpgradeStatus>(`/tenants/${tenantId}/workspaces/${workspaceId}/upgrade/status`),
    enabled: Boolean(tenantId && workspaceId) && enabled,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'queued' || s === 'running' ? 5_000 : false;
    },
  });
}

export interface ProvisioningQueueEntry extends WorkspaceProgress {
  tenant_slug: string | null;
  tenant_name: string | null;
  workspace_name: string;
  hostname: string | null;
}

/** Every in-flight or failed provision run, fleet-wide. */
export function useProvisioningQueue() {
  return useQuery({
    queryKey: ['provisioning', 'active'],
    queryFn: () => apiFetch<ProvisioningQueueEntry[]>('/tenants/provisioning/active'),
    refetchInterval: (query) => {
      const data = query.state.data;
      const anyInFlight = data?.some(
        (e) => e.provision_status !== 'live' && e.provision_status !== 'failed',
      );
      return anyInFlight ? 5_000 : 30_000;
    },
  });
}

export function useRemoveCustomDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, workspaceId, domain }: {
      tenantId: string; workspaceId: string; domain: string;
    }) =>
      apiFetch(`/tenants/${tenantId}/workspaces/${workspaceId}/domains/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, { tenantId, workspaceId }) => {
      qc.invalidateQueries({ queryKey: keys.detail(tenantId) });
      qc.invalidateQueries({ queryKey: keys.workspace(tenantId, workspaceId) });
    },
  });
}
